import { createContext, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useApiHook from "../hooks/useApiHook";
import { SocialLogin } from "@capgo/capacitor-social-login";
import config from "../config";
import { Preferences } from "@capacitor/preferences";
import useAppView from "../components/platforms/useAppView";

const AuthContext = createContext({
    user: null,
    response: null,
    token : null,
    isLoading: false,
    isAuthenticated: false,
    googleAuth: null,
    googleMobileLogin: () => { }
})

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [response, setResponse] = useState(null);
    const [token, setToken] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const navigate = useNavigate();
    const { response: responseSuccess, error: responseError, makeRequest } = useApiHook();
    const { isNativePlatform } = useAppView();

    useEffect(() => {
        if (isNativePlatform) {
            SocialLogin.initialize({
                google: {
                    webClientId: config.GOOGLE_OAUTH_CLIENT_ID,
                },
            });
        }
    }, [isNativePlatform]);

    const updateIsAuthenticated = (value) => {
        setIsAuthenticated(value);
        localStorage.setItem('isAuthenticated', value.toString());
    };

    const storeSession = async (newToken) => {
        setToken(newToken);
        if (isNativePlatform) {
            await Preferences.set({key: 'token', value: newToken});
            return;
        }
        localStorage.setItem('token', newToken);
    };

    const deleteSession = async () => {
        setToken(null);
        if (isNativePlatform) {
            await Preferences.remove({key: 'token'});
            return;
        }
        localStorage.removeItem('token');
    };

    const readSession = async () => {
        let sessionToken;
        if (isNativePlatform) {
            const { value } = await Preferences.get({key: 'token'});
            sessionToken = value;
        } else {
            sessionToken = localStorage.getItem('token');
        }
        setToken(sessionToken);
        return sessionToken;
    };

    useEffect(() => {
        const tokenCheck = async () => {
            const authToken = await readSession();
            if (!authToken) {
                setIsLoading(false);
                return;
            }
            makeRequest('/auth/autologin', 'post', { requestType: 'autologin' }, { token: authToken });
        }
        tokenCheck();
    }, []);

    useEffect(() => {
        if (!responseError) return;
        setResponse(responseError);
        setIsLoading(false);
        (async () => {
            const statusCode = responseError?.response?.status || responseError?.status;
            if (statusCode === 401 || statusCode === 403) {
                console.log(`[AUTH] Errore ${statusCode} rilevato - logout automatico`);
                setUser(null);
                updateIsAuthenticated(false);
                await deleteSession();
                navigate("/auth");
            }
        })();

    }, [responseError]);

    useEffect(() => {
        const res = responseSuccess;
        if (!res || !res.request || !res.request.requestType) return;

        setIsLoading(false);

        switch (res.request.requestType) {
            case 'register':
                setResponse(res);
                break;
            case 'login':
            case 'google':
                setResponse(res);
                if (res.state === 'success') {
                    storeSession(res.data.token);
                    setUser(res.data.user);
                    updateIsAuthenticated(true);
                    navigate("/");
                } else {
                    deleteSession();
                    setUser(null);
                    updateIsAuthenticated(false);
                    navigate("/");
                }
                break;
            case 'autologin':
                setResponse(res);
                if (res.state === 'success') {
                    storeSession(res.data.token);
                    setUser(res.data.user);
                    updateIsAuthenticated(true);
                } else {
                    deleteSession();
                    setUser(null);
                    updateIsAuthenticated(false);
                }
                break;
            case 'logout':
                break;
        }
    }, [responseSuccess]);

    const register = (email, password, name, lastname) => {
        makeRequest('/auth/register', 'post', { email, password, name, lastname, requestType: 'register' });
    }

    const login = (email, password) => {
        makeRequest('/auth/login', 'post', { email, password, requestType: 'login' });
    }

    const logout = () => {
        setUser(null);
        updateIsAuthenticated(false);
        localStorage.removeItem('token');
        navigate("/");
    }

    const updateUser = (updatedUserData) => {
        setUser(prev => ({
            ...prev,
            ...updatedUserData
        }));
    }

    const googleAuth = (credentialResponse) => {
        makeRequest('/auth/google', 'post', {
            idToken: credentialResponse.credential,
            requestType: 'login'
        });
    }

    const googleMobileLogin = async () => {
        const { result } = await SocialLogin.login({
            provider: 'google',
        });

        makeRequest('/auth/google', 'post', {
            idToken: result.idToken,
            requestType: 'login'
        });
    }

    return (
        <AuthContext.Provider value={{ user, token, response, isLoading, isAuthenticated, register, login, logout, updateUser, googleAuth, googleMobileLogin }}>
            {children}
        </AuthContext.Provider>
    )
}

export const useAuth = () => useContext(AuthContext);
